import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import Box from 'ravenjs/lib/Box';
import Button from 'ravenjs/lib/Button';
import Col from 'ravenjs/lib/Col';
import Divider from 'ravenjs/lib/Divider';
import Menu from 'ravenjs/lib/Menu';
import Row from 'ravenjs/lib/Row';
import Typography from 'ravenjs/lib/Typography';
import Input from 'ravenjs/lib/Input';
import { get } from 'ravenjs/utils/lodash';
import _isEmpty from 'lodash/isEmpty';

import _findIndex from 'lodash/findIndex';
import { addEventListenerOnRoot, callFunc, removeEventListenerOnRoot } from 'ravenjs/utils';
import { themeGet } from 'ravenjs/utils/theme';
import { THEME } from 'ravenjs/constants/theme';

import ArrowDownIcon from 'components/Icons/ArrowDownIcon';
import REGEX_VALIDATE from 'constants/regex';

const ButtonComponentStyled = styled(Col)`
    color: ${themeGet('palette.common.lighter')};
    cursor: pointer;
    padding: 8px 10px;
    position: relative;
`;

const StyledInput = styled(Input)`
    ${props =>
        props.type === 'number' &&
        `
      /* Hide the up and down arrows in WebKit browsers */
      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      
      /* Hide the up and down arrows in Firefox */
      -moz-appearance: textfield;
    `}
`;

function AdvancedSearchMenu(props) {
    const { menuItems, onChange, onClick, onOpen, onClose, onClear, searchSpec } = props;
    const menuEl = React.useRef(null);
    const inputEl = React.useRef(null);
    const dropdownIconRef = React.useRef(null);

    const [inputFocused, setFocusOnInput] = React.useState(false);
    const [advanceSearchString, setAdvanceSearchString] = React.useState(searchSpec);
    const [menuItemsSpec, setMenuItemsSpec] = React.useState(menuItems);
    const [onOpenMenu, setonOpenMenu] = React.useState(null);
    const [initialSearchSpec, setInitialSearchSpec] = React.useState(null);
    const ContentProps = {
        elevation: 10,
        margin: '5px 0 0',
        padding: '0',
    };
    const textColor = themeGet('palette.primary.color', '#7F8FA4')({ theme: THEME });

    function handleInputOnChange(event) {
        if (
            event.target.value &&
            event.target?.dataset?.pattern === 'numeric' &&
            !REGEX_VALIDATE.NUMBER_ONLY.test(event.target.value)
        ) {
            return;
        }

        const labelName = event.target.name;
        const currentIndex = _findIndex(menuItemsSpec, { label: labelName });

        if (currentIndex !== -1) {
            const menuItemsWithNewValues = menuItemsSpec.map(item => {
                if (item.label === labelName) {
                    return {
                        ...item,
                        value: event.target.value,
                    };
                }
                return item;
            });

            // Replace the new input data for the Menu Item Key
            menuItemsSpec[currentIndex].value = event.target.value;

            const updateSearchString = menuItemsWithNewValues.reduce((acc, option) => {
                return `${acc} ${option.value ? ` ${option.key} : "${option.value}"` : ''}`;
            }, '');

            setAdvanceSearchString(updateSearchString);
            callFunc(onChange, updateSearchString);
        }
    }

    function handleInputOnFocus() {
        setFocusOnInput(true);
    }

    function getInputValue(label) {
        const currentIndex = _findIndex(menuItemsSpec, { label });
        if (currentIndex !== -1) {
            const inputValue = menuItemsSpec[currentIndex].value;
            return inputValue || '';
        }
        return '';
    }

    function handleBtnOnClick(event) {
        event.preventDefault();
        setonOpenMenu(false);
        callFunc(onClick, menuItemsSpec, advanceSearchString);
    }

    function handleMenuOpen() {
        const isOpen = !onOpenMenu;
        if (isOpen) {
            callFunc(onOpen);
        } else {
            callFunc(onClose);
        }
        setonOpenMenu(isOpen);
    }

    const handleMenuClose = React.useCallback(
        event => {
            const { current } = get(menuEl, 'current.menuContentRef', {});
            // Do not close Menu if clear search button clicked
            if (event.target.id === 'clear') {
                const clearMenuInputs = menuItemsSpec.map(menu => {
                    return {
                        ...menu,
                        value: '',
                    };
                });
                setMenuItemsSpec(clearMenuInputs);
                setAdvanceSearchString('');
                setInitialSearchSpec('');
                if (!_isEmpty(initialSearchSpec)) {
                    callFunc(onClick, clearMenuInputs, '');
                } else {
                    callFunc(onClear, event, clearMenuInputs, advanceSearchString);
                }
            }
            // Close the menu only if we click outside the menu's content.
            else if (
                current &&
                event.target &&
                !current.contains(event.target) &&
                dropdownIconRef &&
                !dropdownIconRef.current.contains(event.target)
            ) {
                setonOpenMenu(false);
                callFunc(onClose);
            }
        },
        [advanceSearchString, initialSearchSpec, menuItemsSpec, onClear, onClick, onClose]
    );

    useEffect(() => {
        addEventListenerOnRoot('click', handleMenuClose);
        setInitialSearchSpec(searchSpec);
        return () => {
            setInitialSearchSpec('');
            removeEventListenerOnRoot('click', handleMenuClose);
        };
    }, [handleMenuClose, searchSpec]);

    const buttonProps = {
        onClick: handleMenuOpen,
    };

    const PopperProps = {
        container: dropdownIconRef,
        style: {
            zIndex: 1,
        },
    };

    function handleKeyDown(event, inputType) {
        if (inputType === 'number' && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
            event.preventDefault();
        } else if (event.keyCode === 13) {
            handleBtnOnClick(event);
        }
    }

    return (
        <Menu
            ButtonComponent={
                <ButtonComponentStyled ref={dropdownIconRef}>
                    <ArrowDownIcon style={{ margin: '6px 0 0' }} />
                </ButtonComponentStyled>
            }
            ButtonProps={buttonProps}
            ContentComponent={Box}
            ContentProps={ContentProps}
            IconComponent={null}
            onClose={onClose}
            onOpen={onOpen}
            open={onOpenMenu}
            placement="bottom-end"
            PopperProps={PopperProps}
            ref={menuEl}
            timeout={100}
        >
            <Box
                backgroundColor="transparent"
                borderRadius="0"
                borderWidth="0"
                margin="0"
                padding="15px"
                width="273px"
            >
                <Row gutter={false} justify="space-between">
                    <Typography color="advancedSearch" gutterBottom="0">
                        Advanced Search
                    </Typography>
                    <Typography
                        onClick={handleMenuClose}
                        align="right"
                        gutterBottom="0"
                        type="link"
                        id="clear"
                    >
                        Clear Search
                    </Typography>
                </Row>
                <Divider />
                <Row gutter={false} direction="column">
                    {menuItemsSpec.map(item => {
                        const { label, inputType, dataPattern, key, ...rest } = item;
                        return (
                            <Box
                                backgroundColor="transparent"
                                borderRadius="0"
                                borderWidth="0"
                                key={item.label}
                                padding="0"
                                focused={inputFocused}
                            >
                                <Typography color="advancedSearch" gutterLeft="5px">
                                    {item.label}
                                </Typography>
                                <StyledInput
                                    key={key}
                                    {...rest}
                                    onChange={handleInputOnChange}
                                    onFocus={handleInputOnFocus}
                                    name={item.label}
                                    onKeyDown={e => handleKeyDown(e, inputType)}
                                    ref={inputEl}
                                    style={{ color: textColor }}
                                    value={getInputValue(item.label)}
                                    type={inputType ?? 'text'}
                                    data-pattern={dataPattern}
                                />
                            </Box>
                        );
                    })}
                    <Row gutter={false} justify="flex-end">
                        <Button onClick={handleBtnOnClick} color="secondary" noMinWidth>
                            Search
                        </Button>
                    </Row>
                </Row>
            </Box>
        </Menu>
    );
}

AdvancedSearchMenu.propTypes = {
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onClear: PropTypes.func,
    searchBoxRef: PropTypes.object,
    searchSpec: PropTypes.string,
    menuItems: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            placeholder: PropTypes.string,
            onChange: PropTypes.func,
            onClick: PropTypes.func,
            value: PropTypes.string,
        })
    ),
};

AdvancedSearchMenu.defaultProps = {
    onOpen: null,
    onClose: null,
    onChange: null,
    onClick: null,
    onClear: null,
    searchBoxRef: null,
    searchSpec: null,
    menuItems: [],
};

export default AdvancedSearchMenu;
